import React, { useState, useEffect } from "react";
import { HelpCircle, X, Send, CheckCircle2, ChevronDown, AlertCircle } from "lucide-react";

const HELP_TYPES = [
  { value: "bug", label: "Bug — something is broken" },
  { value: "feature", label: "Feature request" },
  { value: "question", label: "Question / how do I…" },
  { value: "billing", label: "Billing or account" },
  { value: "other", label: "Other" },
];

const HelpModal = ({ open, onClose, user }) => {
  const [type, setType] = useState("bug");
  const [subject, setSubject] = useState("");
  const [desc, setDesc] = useState("");
  const [includeUrl, setIncludeUrl] = useState(true);
  const [showErr, setShowErr] = useState(false);
  const [ticket, setTicket] = useState(null);

  useEffect(() => {
    if (open) {
      setType("bug");
      setSubject("");
      setDesc("");
      setIncludeUrl(true);
      setShowErr(false);
      setTicket(null);
    }
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const valid = subject.trim() && desc.trim();
  const submit = (e) => {
    e.preventDefault();
    if (!valid) {
      setShowErr(true);
      return;
    }
    // TODO: wire to a real support/report API. For now generate a reference.
    setTicket("SR-" + String(Date.now()).slice(-6));
  };

  const firstName = String(user?.name || "").split(" ")[0] || "there";

  return (
    <div
      className="fixed inset-0 z-[200] bg-s360-ink/45 backdrop-blur-[2px] flex items-start justify-center px-5 py-14 overflow-y-auto"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-[520px] bg-white rounded-[10px] shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {/* Head */}
        <header className="flex items-center gap-3 px-[18px] py-4 border-b border-s360-stroke1 bg-gradient-to-b from-[#FBFAFF] to-white">
          <span className="w-[38px] h-[38px] rounded-lg bg-s360-violet-50 text-s360-violet inline-flex items-center justify-center shrink-0">
            <HelpCircle className="w-[18px] h-[18px]" />
          </span>
          <div className="flex flex-col min-w-0">
            <h2 className="text-[18px] leading-6 font-bold text-s360-ink m-0">
              {ticket ? "Report submitted" : "Report an issue"}
            </h2>
            <span className="text-[13px] text-s360-fg3">
              {ticket ? "We've logged your report" : "Tell us what went wrong or what you need"}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto w-[34px] h-[34px] rounded-lg inline-flex items-center justify-center text-s360-fg3 hover:bg-s360-bg2 hover:text-s360-fg1"
          >
            <X className="w-[18px] h-[18px]" />
          </button>
        </header>

        {ticket ? (
          <div className="px-6 py-8 flex flex-col items-center text-center">
            <span className="w-[60px] h-[60px] rounded-full bg-s360-success-bg text-s360-success inline-flex items-center justify-center mb-3.5 ring-[6px] ring-[#EAF8E8]">
              <CheckCircle2 className="w-[30px] h-[30px]" />
            </span>
            <h3 className="text-xl font-bold text-s360-ink mb-1.5">Thanks, {firstName}!</h3>
            <p className="text-sm text-s360-fg3 mb-3.5 max-w-[38ch]">
              Your report has been submitted. Our team will follow up at{" "}
              <strong className="text-s360-fg1">{user?.email || "your email"}</strong>.
            </p>
            <div className="text-[13px] text-s360-fg2 bg-s360-bg2 border border-dashed border-s360-stroke2 rounded-md px-3.5 py-2 mb-4">
              Reference <b className="font-mono text-s360-ink">{ticket}</b>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-s360-violet text-white text-sm font-semibold hover:bg-s360-violet-600"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="p-[18px] flex flex-col gap-3.5">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-s360-fg2">Issue type</label>
                <div className="relative">
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="w-full h-10 px-3 pr-9 bg-white border border-s360-stroke1 rounded-md text-sm text-s360-fg1 outline-none appearance-none cursor-pointer focus:border-s360-violet focus:ring-2 focus:ring-s360-violet/20"
                  >
                    {HELP_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 text-s360-fg3 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-s360-fg2">Subject</label>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Brief summary of the issue"
                  className={`h-10 px-3 bg-white border rounded-md text-sm text-s360-fg1 outline-none focus:border-s360-violet focus:ring-2 focus:ring-s360-violet/20 placeholder:text-s360-fg-disabled ${
                    showErr && !subject.trim() ? "border-s360-danger" : "border-s360-stroke1"
                  }`}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-s360-fg2">Description</label>
                <textarea
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  rows={5}
                  placeholder="What happened? What did you expect? Steps to reproduce help us a lot."
                  className={`px-3 py-2 bg-white border rounded-md text-sm text-s360-fg1 outline-none resize-y leading-relaxed focus:border-s360-violet focus:ring-2 focus:ring-s360-violet/20 placeholder:text-s360-fg-disabled ${
                    showErr && !desc.trim() ? "border-s360-danger" : "border-s360-stroke1"
                  }`}
                />
              </div>

              <label className="flex items-center gap-2 text-[13px] text-s360-fg2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeUrl}
                  onChange={(e) => setIncludeUrl(e.target.checked)}
                  className="w-[15px] h-[15px] accent-s360-violet cursor-pointer"
                />
                Include current page &amp; browser details
              </label>

              {showErr && !valid && (
                <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-s360-danger bg-s360-danger-bg border border-[#F1BBC0] rounded-md px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5" /> Please add a subject and description.
                </div>
              )}
            </div>

            <footer className="flex justify-end gap-2.5 px-[18px] py-3.5 border-t border-s360-stroke1 bg-[#FBFAFF]">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg2 hover:bg-s360-bg2"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600"
              >
                <Send className="w-3.5 h-3.5" /> Submit report
              </button>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
};

export default HelpModal;
